import React, { useEffect, useRef, useState } from 'react';
import SlideVisualStage, { pageHeightOf } from '../components/SlideVisualStage';
import { HANDLES, moveElement, pixelsToUnits, resizeElement } from './studioGeometry';
import type { HandleDirection } from './studioGeometry';
import { firstRun, textOf } from './studioText';
import type { SlideElement, SlideVisual } from '../types';
import { SLIDE_DESIGN_WIDTH } from '../types';

interface Props {
  visual: SlideVisual;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Vervangt één element (tijdens slepen en schalen, live). */
  onElementChange: (element: SlideElement) => void;
  editingId: string | null;
  onEditingChange: (id: string | null) => void;
  onCommitText: (elementId: string, value: string) => void;
}

type DragStart = { kind: 'move'; origin: SlideElement } | { kind: 'resize'; origin: SlideElement; handle: HandleDirection };
type Drag = DragStart & { startX: number; startY: number };

const pct = (value: number, total: number) => `${((value / total) * 100).toFixed(4)}%`;

/**
 * De bewerkbare slide: dezelfde weergave als de beamer (SlideVisualStage), met daarbovenop selectie,
 * slepen, schalen met grepen en inline tekstbewerking. Alle wijzigingen gaan via de callbacks naar de pagina.
 */
export default function SlideCanvasEditor({
  visual, selectedId, onSelect, onElementChange, editingId, onEditingChange, onCommitText,
}: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState(false);
  const pageHeight = pageHeightOf(visual);
  const selected = (visual.elements || []).find((element) => element.id === selectedId) || null;

  const unitsPerPixelFactor = () => {
    const width = stageRef.current?.getBoundingClientRect().width || 0;
    return (pixels: number) => pixelsToUnits(pixels, width, SLIDE_DESIGN_WIDTH);
  };

  const beginDrag = (event: React.PointerEvent, drag: DragStart) => {
    event.stopPropagation();
    event.preventDefault();
    dragRef.current = { ...drag, startX: event.clientX, startY: event.clientY };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const toUnits = unitsPerPixelFactor();
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = toUnits(event.clientX - drag.startX);
      const dy = toUnits(event.clientY - drag.startY);
      onElementChange(drag.kind === 'move' ? moveElement(drag.origin, dx, dy) : resizeElement(drag.origin, drag.handle, dx, dy));
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  const frameStyle = (element: SlideElement): React.CSSProperties => ({
    position: 'absolute', left: pct(element.x, SLIDE_DESIGN_WIDTH), top: pct(element.y, pageHeight),
    width: pct(element.width, SLIDE_DESIGN_WIDTH), height: pct(element.height, pageHeight),
    transform: `rotate(${element.rotation || 0}deg)`, transformOrigin: 'center', boxSizing: 'border-box',
  });

  const editing = (visual.elements || []).find((element) => element.id === editingId && element.text !== undefined) || null;

  return (
    <SlideVisualStage
      ref={stageRef} visual={visual} previewMode hiddenElementId={editing?.id}
      onBackgroundPointerDown={() => { onSelect(null); onEditingChange(null); }}
      onElementDoubleClick={(element) => { if (element.text) onEditingChange(element.id); }}
      onElementActivate={(element) => { onSelect(element.id); if (editingId) onEditingChange(null); }}
      onElementPointerDown={(event, element) => {
        if (editingId === element.id) return; // klikken in de tekst zelf
        onSelect(element.id);
        if (editingId) onEditingChange(null);
        beginDrag(event, { kind: 'move', origin: element });
      }}
    >
      {selected && !editing && (
        <div style={{ ...frameStyle(selected), pointerEvents: 'none', outline: '2px solid #14b8a6' }} data-testid="selection-frame">
          {HANDLES.map((handle) => (
            <span
              key={handle.id} data-handle={handle.id} role="presentation"
              onPointerDown={(event) => beginDrag(event, { kind: 'resize', origin: selected, handle })}
              style={{
                position: 'absolute', pointerEvents: 'auto', width: 12, height: 12, background: '#fff', border: '2px solid #14b8a6',
                borderRadius: 2, boxSizing: 'border-box', transform: 'translate(-50%, -50%)',
                left: `${(handle.hx + 1) * 50}%`, top: `${(handle.hy + 1) * 50}%`,
                cursor: `${handle.id}-resize`,
              }}
            />
          ))}
        </div>
      )}
      {editing && (
        <textarea
          aria-label="Tekst" autoFocus defaultValue={textOf(editing)}
          onPointerDown={(event) => event.stopPropagation()}
          onBlur={(event) => { onCommitText(editing.id, event.target.value); onEditingChange(null); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' || (event.key === 'Enter' && (event.ctrlKey || event.metaKey))) {
              event.preventDefault();
              (event.target as HTMLTextAreaElement).blur();
            }
          }}
          style={{
            ...frameStyle(editing), minHeight: '8%', resize: 'none', background: '#f8fafc', color: '#111',
            border: '2px solid #14b8a6', padding: '0.5cqw', fontSize: `${((firstRun(editing)?.font_size || 28) / 10).toFixed(3)}cqw`,
            lineHeight: 1.2, textAlign: editing.text?.paragraphs[0]?.align === 'justify' ? 'left' : editing.text?.paragraphs[0]?.align || 'left',
            fontFamily: 'Arial, Helvetica, sans-serif', outline: 'none', overflow: 'auto',
          }}
        />
      )}
    </SlideVisualStage>
  );
}
